import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sprout, ShieldCheck, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FeaturedFarms — public landing-page section showing real hobby farmers from /api/hobby-farmers.
 * Uses a direct axios call (no withCredentials) since this is a public, anonymous endpoint.
 */
export default function FeaturedFarms({ limit = 6 }) {
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/hobby-farmers`)
      .then(({ data }) => setFarms((data || []).slice(0, limit)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [limit]);

  if (loading || farms.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-12 py-24" data-testid="featured-farms">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Featured Hobby Farmers</div>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-stone-900">Meet the meat &amp; veggie farms.</h2>
        </div>
        <Link
          to="/hobby-farmers"
          data-testid="featured-see-all"
          className="font-sans text-sm text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1.5"
        >
          See every farm <ArrowRight size={14} />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {farms.map((f) => (
          <Link
            key={f.user_id}
            to={`/hobby-farmers/${f.user_id}`}
            data-testid={`featured-farm-${f.user_id}`}
            className="group bg-white border border-stone-200 rounded-md overflow-hidden lift"
          >
            <div className="aspect-[16/10] bg-stone-100 overflow-hidden">
              {f.profile_image_url ? (
                <img
                  src={f.profile_image_url}
                  alt={f.farm_name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-400">
                  <Sprout size={32} strokeWidth={1.2} />
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-serif text-xl text-stone-900 leading-tight">{f.farm_name}</h3>
                {f.verified && (
                  <Badge className="bg-emerald-800 text-stone-50 text-[10px] uppercase tracking-wider">
                    <ShieldCheck size={10} className="mr-1" />Verified
                  </Badge>
                )}
              </div>
              <div className="mt-1.5 font-sans text-xs text-stone-500">
                {[f.city, f.state, f.country].filter(Boolean).join(", ")}
              </div>
              {f.services && (
                <p className="mt-3 font-sans text-sm text-stone-600 line-clamp-2">{f.services}</p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
